<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { DAILY_TOKEN_GOAL } from "@/shared/config/constants";
import { compact } from "@/shared/utils/format";
import { buildHeatmapWeeks, heatmapStats, GOAL_LEVEL } from "@/features/leaderboard/model/heatmap";

const props = defineProps({
  year: { type: [Number, String], required: true },
  uploads: { type: Array, default: () => [] },
  selectedDate: { type: String, default: null },
  today: { type: String, required: true },
  goal: { type: Number, default: DAILY_TOKEN_GOAL },
});
const emit = defineEmits(["select"]);

const yearNumber = computed(() => Number(props.year));
const weeks = computed(() =>
  buildHeatmapWeeks({
    year: yearNumber.value,
    uploads: props.uploads,
    today: props.today,
    selectedDate: props.selectedDate,
    goal: props.goal,
  })
);
const stats = computed(() => heatmapStats(props.uploads, yearNumber.value, props.goal));
const goalLabel = computed(() => compact.format(props.goal));

// 检视行：悬停优先，否则显示当前选中的那天。
const hoverDate = ref(null);
const inspected = computed(() => {
  const date = hoverDate.value ?? props.selectedDate;
  if (!date) return null;
  for (const column of weeks.value.columns) {
    const day = column.days.find((item) => item && item.date === date);
    if (day) return day;
  }
  return null;
});
const inspectorText = computed(() => {
  const day = inspected.value;
  if (!day) return "";
  if (day.isFuture) return "还没到";
  if (!day.totalTokens) return day.isToday ? "今天还没有上传" : "没有上传";
  return `${compact.format(day.totalTokens)} token${day.level === GOAL_LEVEL ? " · 已达标" : ""}`;
});

const cellClass = (day) => [`lv${day.level}`, { future: day.isFuture, today: day.isToday, selected: day.isSelected }];
const cellLabel = (day) => {
  if (day.isFuture) return `${day.date} 还没到`;
  if (!day.totalTokens) return `${day.date} 没有上传`;
  return `${day.date} ${compact.format(day.totalTokens)} token`;
};
function pick(day) {
  if (day.isFuture) return;
  emit("select", day.date);
}

// 整条默认停在「最新一周」= 今天（或选中日）那一列贴右边，而不是年底那堆还没到的格子；
// 换年重新定位，切换日期时把选中格滚进视野。
const SCROLL_EDGE = 18;
const scroller = ref(null);
async function scrollToLatest() {
  await nextTick();
  const el = scroller.value;
  if (!el) return;
  const anchor = el.querySelector(".heatmap-cell.selected") ?? el.querySelector(".heatmap-cell.today");
  if (!anchor) {
    el.scrollLeft = el.scrollWidth;
    return;
  }
  el.scrollLeft = Math.max(0, anchor.offsetLeft + anchor.offsetWidth + SCROLL_EDGE - el.clientWidth);
}
async function scrollSelectedIntoView() {
  await nextTick();
  const cell = scroller.value?.querySelector(".heatmap-cell.selected");
  if (cell && typeof cell.scrollIntoView === "function") {
    cell.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
}
onMounted(scrollToLatest);
watch(yearNumber, scrollToLatest);
watch(() => props.selectedDate, scrollSelectedIntoView);
</script>

<template>
  <section class="heatmap" aria-label="我的 token 热力图">
    <div class="heatmap-head">
      <span class="heatmap-title">{{ yearNumber }} 年 · 我的 token 热力图</span>
      <span class="heatmap-stats">上传 {{ stats.uploadedDays }} 天 · 达标 {{ stats.goalDays }} 天</span>
      <span class="heatmap-inspector" aria-live="polite">
        <template v-if="inspected"><b>{{ inspected.date }}</b>{{ inspectorText }}</template>
      </span>
    </div>
    <div class="heatmap-body">
      <div class="heatmap-weekdays" aria-hidden="true">
        <span>一</span><span></span><span>三</span><span></span><span>五</span><span></span><span>日</span>
      </div>
      <div ref="scroller" class="heatmap-scroll" @mouseleave="hoverDate = null">
        <div class="heatmap-inner">
          <div class="heatmap-months" aria-hidden="true">
            <span v-for="month in weeks.monthLabels" :key="month.col" :style="{ gridColumn: month.col + 1 }">
              {{ month.label }}
            </span>
          </div>
          <div class="heatmap-grid">
            <template v-for="column in weeks.columns" :key="column.col">
              <template v-for="(day, row) in column.days" :key="`${column.col}-${row}`">
                <span v-if="!day" class="heatmap-cell empty" aria-hidden="true" />
                <button
                  v-else
                  type="button"
                  class="heatmap-cell"
                  :class="cellClass(day)"
                  :disabled="day.isFuture"
                  :aria-label="cellLabel(day)"
                  :aria-pressed="day.isSelected"
                  @click="pick(day)"
                  @mouseenter="hoverDate = day.date"
                  @focus="hoverDate = day.date"
                />
              </template>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="heatmap-legend" aria-hidden="true">
      <span>少</span>
      <i class="lv0" /><i class="lv1" /><i class="lv2" /><i class="lv3" /><i class="lv4" />
      <span>多</span>
      <span class="sep">·</span>
      <span>全黑 = 达标（≥ {{ goalLabel }}）</span>
      <span class="sep">·</span>
      <span>行 = 周一到周日 · 横向滑动看全年</span>
    </div>
  </section>
</template>
